import { UserRef } from '@rygen/rygen-ui/model';

import {
  BatchCheckCallActionResponse,
  BulkTenderCommand,
  BulkTenderResult,
  HttpHeader,
  ManualShipment,
  ShipmentHealth,
  ShipmentInviteCarriersToSpotMarketBatchCommand,
  SpotMarket,
  TenderAcceptRequest,
  TenderRejectRequest,
  TenderRequest,
  UserAssignmentType,
} from '@/model';

import { BaseHttpController } from './BaseHttpController';

const endpoint = 'batch/shipments';

export class ShipmentBatchController extends BaseHttpController {
  constructor() {
    super(endpoint);
  }

  async assignUsers(
    shipmentIds: number[],
    assignedUsers: UserRef[],
    type: UserAssignmentType,
  ): Promise<number[]> {
    const response = await this.http.post(`users`, {
      shipmentIds,
      assignedUsers,
      type,
    });
    return response.data;
  }

  async create(
    shipment: ManualShipment,
    numShipments: number,
  ): Promise<number[]> {
    if (shipment.id) {
      throw new Error('Id expected to be null when calling create');
    }

    const response = await this.http.post<number[]>(
      '',
      {
        shipment,
        numShipments,
      },
      {
        headers: {
          [HttpHeader.SOURCE]: 'Shipment Entry',
        },
      },
    );
    return response.data;
  }

  async tenderAcceptBatch(
    shipmentIds: Array<number>,
    tenderAcceptRequest: TenderAcceptRequest,
  ): Promise<TenderRequest> {
    const response = await this.http.post<TenderRequest>(
      `${shipmentIds}/tender/accept`,
      tenderAcceptRequest,
    );
    return new TenderRequest(response.data);
  }

  async tenderRejectBatch(
    shipmentIds: Array<number>,
    tenderRejectRequest: TenderRejectRequest,
  ): Promise<TenderRequest> {
    const response = await this.http.post<TenderRequest>(
      `${shipmentIds}/tender/reject`,
      tenderRejectRequest,
    );
    return new TenderRequest(response.data);
  }

  async batchCarrierUnassign(shipmentIds: Array<number>): Promise<number[]> {
    const response = await this.http.post<number[]>(
      `${shipmentIds}/carrier/unassign`,
    );
    return response.data;
  }

  async addTags(ids: number[], tags: string[]): Promise<number[]> {
    const response = await this.http.put<number[]>('tags', { ids, tags });
    return response.data;
  }

  async updateHealth(ids: number[], health: ShipmentHealth): Promise<number[]> {
    const response = await this.http.put<number[]>('health', { ids, health });
    return response.data;
  }

  async batchSendCheckCallReminders(
    shipmentIds: number[],
  ): Promise<BatchCheckCallActionResponse> {
    const response = await this.http.post<BatchCheckCallActionResponse>(
      `${shipmentIds}/check-call-reminders`,
    );
    return new BatchCheckCallActionResponse(response.data);
  }

  async batchCancelCheckCallReminders(
    shipmentIds: number[],
  ): Promise<BatchCheckCallActionResponse> {
    const response = await this.http.post<BatchCheckCallActionResponse>(
      `${shipmentIds}/check-call-reminders/cancel`,
    );
    return new BatchCheckCallActionResponse(response.data);
  }

  async inviteCarriersToSpotMarket(
    command: ShipmentInviteCarriersToSpotMarketBatchCommand,
  ): Promise<SpotMarket[]> {
    const response = await this.http.post<SpotMarket[]>(
      `spot-market/invite`,
      command,
    );
    return response.data.map((s) => new SpotMarket(s));
  }

  async bulkTender(command: BulkTenderCommand): Promise<BulkTenderResult[]> {
    const response = await this.http.post<BulkTenderResult[]>(
      'bulk-tender',
      command,
    );
    return response.data.map((val) => new BulkTenderResult(val));
  }
}
